import { useState, useEffect } from "react";

import axios from "axios";

// react-router components
import { Link } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import CheckIcon from "@mui/icons-material/Check";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import { useTranslation } from "react-i18next";

import Logo from "assets/images/Logo.png";
import LanguageIcon from "@mui/icons-material/Language";
import CNFlag from "assets/images/flags/cn.png";
import GBFlag from "assets/images/flags/gb.png";
import TWFlag from "assets/images/flags/tw.png";

// Material Kit 2 React example components
import NavbarDropdown from "./NavbarDropdown";
import NavbarMobile from "./NavbarMobile";
import NavbarMenu from "./NavbarMenu";
import WechatModal from "./WechatModal";

import NavbarFrame from "./NavbarFrame";
import API_BASE_URL from "config/api";
import { getRoutes as getNavRoutes } from "routes";

// Material Kit 2 React base styles
import breakpoints from "assets/theme/base/breakpoints";

function Navbar({ transparent, light, action, sticky, relative, center, showMenu }) {
  const [dropdown, setDropdown] = useState("");
  const [dropdownName, setDropdownName] = useState("");
  const [mobileNavbar, setMobileNavbar] = useState(false);
  const [mobileView, setMobileView] = useState(false);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);

  const [wechatUrl, setWechatUrl] = useState("");
  const [wechatModalOpen, setWechatModalOpen] = useState(false);

  const { i18n } = useTranslation();

  // Build navbar items from the current language at runtime (do NOT rely on the static `routes` prop)
  const [navRoutes, setNavRoutes] = useState(() =>
    getNavRoutes(i18n.resolvedLanguage || i18n.language)
  );

  useEffect(() => {
    setNavRoutes(getNavRoutes(i18n.resolvedLanguage || i18n.language));
  }, [i18n.language]);

  useEffect(() => {
    // Ensure navbar reflects the persisted language choice across the whole site.
    let persisted = "";

    try {
      persisted = localStorage.getItem("i18nextLng") || localStorage.getItem("i18nLanguage") || "";
    } catch (e) {
      // ignore
    }

    if (!persisted) {
      const m = document.cookie.match(/(?:^|;\s*)i18next=([^;]+)/);
      if (m && m[1]) persisted = decodeURIComponent(m[1]);
    }

    if (persisted && persisted !== (i18n.resolvedLanguage || i18n.language)) {
      i18n.changeLanguage(persisted).catch(() => {
        // ignore
      });
    }
  }, []);

  const handleChangeLanguage = async (lng) => {
    if (!lng) return;

    // Persist using i18next conventions so all pages/components follow the same value.
    try {
      localStorage.setItem("i18nextLng", lng);
      // Backward-compat: keep the old key if something else still reads it.
      localStorage.setItem("i18nLanguage", lng);
      // Some setups use cookie-based detection.
      document.cookie = `i18next=${encodeURIComponent(lng)};path=/;max-age=31536000`;
    } catch (e) {
      console.warn("Failed to persist language", e);
    }

    try {
      await i18n.changeLanguage(lng);
    } catch (e) {
      // ignore
    }

    setLanguageMenuAnchor(null);
  };

  const handleOpenLanguageMenu = (event) => {
    setLanguageMenuAnchor(event.currentTarget);
  };

  const handleCloseLanguageMenu = () => {
    setLanguageMenuAnchor(null);
  };

  const openMobileNavbar = () => setMobileNavbar(!mobileNavbar);

  useEffect(() => {
    // A function that sets the display state for the DefaultNavbarMobile.
    function displayMobileNavbar() {
      if (window.innerWidth < breakpoints.values.lg) {
        setMobileView(true);
        setMobileNavbar(false);
      } else {
        setMobileView(false);
        setMobileNavbar(false);
      }
    }

    /** 
     The event listener that's calling the displayMobileNavbar function when 
     resizing the window.
    */
    window.addEventListener("resize", displayMobileNavbar);

    // Call the displayMobileNavbar function to set the state with the initial value.
    displayMobileNavbar();

    axios
      .get(`${API_BASE_URL}/web2/`)
      .then((res) => {
        if (res.data && Array.isArray(res.data.wechat_url) && res.data.wechat_url.length > 0) {
          setWechatUrl(res.data.wechat_url[0]);
          // 调试：确认前端确实拿到了二维码地址
          // console.log("[Navbar] wechatUrl =", res.data.wechat_url[0]);
        }
      })
      .catch((error) => {
        // 只在控制台提示一下，避免打断前端渲染
        console.error("[Navbar] failed to load /web2/ wechat_url", error);
      });

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", displayMobileNavbar);
  }, []);

  const openWechatModal = (e) => {
    e.preventDefault();
    if (wechatUrl) setWechatModalOpen(true);
  };

  const renderNavbarItems = navRoutes.map(({ key, name, icon, href, route, collapse }) => (
    <NavbarDropdown
      key={key || name}
      name={name}
      icon={icon}
      href={href}
      route={route}
      collapse={Boolean(collapse)}
      onClick={(event) => {
        // 处理锚点滚动（如 #pricing）
        if (route && route.startsWith("#")) {
          event.preventDefault();
          const targetId = route.substring(1);
          const el = document.getElementById(targetId);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
          }
          // 关闭可能已展开的下拉菜单
          setDropdown(null);
          setDropdownName("");
          return;
        }

        if (!collapse) return;

        const { currentTarget } = event;

        // 如果点击同一个菜单，就收起
        if (dropdownName === name) {
          setDropdown(null);
          setDropdownName("");
        } else {
          setDropdown(currentTarget);
          setDropdownName(name);
        }
      }}
      light={light}
    />
  ));

  return (
    <NavbarFrame sticky={sticky} transparent={transparent} light={light} relative={relative}>
      <MKBox display="flex" justifyContent="space-between" alignItems="center">
        <MKBox display="flex" alignItems="center">
          <MKBox
            component={Link}
            to="/"
            lineHeight={1}
            py={transparent ? 1.5 : 0.75}
            pl={relative || transparent ? 0 : { xs: 0, lg: 1 }}
          >
            <MKBox display="flex" alignItems="center">
              <img
                src={Logo}
                alt="ZionLadder Logo"
                width={32}
                style={{ marginRight: "8px", height: "auto" }}
              />
              <MKTypography
                variant="h5"
                fontWeight="bold"
                color={light ? "white" : "dark"}
                sx={{ fontSize: "1.35rem", display: { xs: "none", lg: "block" } }}
              >
                {["zh", "zh-CN", "zh-TW", "zh-HK"].includes(i18n.language) ? "梯子Z" : "ZionLadder"}
              </MKTypography>
            </MKBox>
          </MKBox>
        </MKBox>
        {showMenu && (
          <MKBox
            color="inherit"
            display={{ xs: "none", lg: "flex" }}
            ml="auto"
            mr={center ? "auto" : 0}
          >
            {renderNavbarItems}
          </MKBox>
        )}
        <MKBox ml={{ xs: "auto", lg: 0 }} display="flex" alignItems="center">
          {action &&
            (action.type === "internal" ? (
              <MKButton
                component={Link}
                to={action.route}
                variant={
                  action.color === "white" || action.color === "default" ? "contained" : "gradient"
                }
                color={action.color ? action.color : "info"}
                size="small"
                onClick={action.onClick}
              >
                {action.label}
              </MKButton>
            ) : (
              <MKButton
                component="a"
                href={action.route}
                target="_blank"
                rel="noreferrer"
                variant={
                  action.color === "white" || action.color === "default" ? "contained" : "gradient"
                }
                color={action.color ? action.color : "info"}
                size="small"
                onClick={action.onClick}
              >
                {action.label}
              </MKButton>
            ))}
          <MKBox ml={1} display="flex" alignItems="center">
            <MKButton
              variant="text"
              color={light ? "white" : "dark"}
              size="large"
              onClick={handleOpenLanguageMenu}
              sx={{
                minWidth: "auto",
                p: 0.5,
              }}
            >
              <LanguageIcon sx={{ fontSize: 24 }} />
            </MKButton>
            <Menu
              anchorEl={languageMenuAnchor}
              open={Boolean(languageMenuAnchor)}
              onClose={handleCloseLanguageMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                onClick={() => handleChangeLanguage("zh-CN")}
                sx={{ py: 0.35, minHeight: 24 }}
              >
                <ListItemIcon sx={{ minWidth: 20 }}>
                  {(i18n.language === "zh" || i18n.language === "zh-CN") && (
                    <CheckIcon sx={{ fontSize: 16 }} />
                  )}
                </ListItemIcon>
                <img
                  src={CNFlag}
                  alt="中文"
                  width={18}
                  style={{ marginRight: 8, height: "auto" }}
                />
                <ListItemText primary="简体中文" primaryTypographyProps={{ variant: "body2" }} />
              </MenuItem>
              <MenuItem
                onClick={() => handleChangeLanguage("zh-TW")}
                sx={{ py: 0.35, minHeight: 24 }}
              >
                <ListItemIcon sx={{ minWidth: 20 }}>
                  {(["zh-TW", "zh-HK", "zh-Hant"].includes(i18n.language) ||
                    ["zh-TW", "zh-HK", "zh-Hant"].includes(i18n.resolvedLanguage)) && (
                    <CheckIcon sx={{ fontSize: 16 }} />
                  )}
                </ListItemIcon>
                <img
                  src={TWFlag}
                  alt="繁體中文"
                  width={18}
                  style={{ marginRight: 8, height: "auto" }}
                />
                <ListItemText primary="繁體中文" primaryTypographyProps={{ variant: "body2" }} />
              </MenuItem>
              <MenuItem onClick={() => handleChangeLanguage("en")} sx={{ py: 0.35, minHeight: 24 }}>
                <ListItemIcon sx={{ minWidth: 20 }}>
                  {i18n.language === "en" && <CheckIcon sx={{ fontSize: 16 }} />}
                </ListItemIcon>
                <img
                  src={GBFlag}
                  alt="English"
                  width={18}
                  style={{ marginRight: 8, height: "auto" }}
                />
                <ListItemText primary="English" primaryTypographyProps={{ variant: "body2" }} />
              </MenuItem>
            </Menu>
          </MKBox>
        </MKBox>
        {showMenu && (
          <MKBox
            display={{ xs: "inline-block", lg: "none" }}
            lineHeight={0}
            py={1.5}
            pl={1.5}
            color={transparent ? "white" : "inherit"}
            sx={{ cursor: "pointer" }}
            onClick={openMobileNavbar}
          >
            <Icon fontSize="default">{mobileNavbar ? "close" : "menu"}</Icon>
          </MKBox>
        )}
      </MKBox>
      <MKBox
        bgColor={transparent ? "white" : "transparent"}
        shadow={transparent ? "lg" : "none"}
        borderRadius="xl"
        px={transparent ? 2 : 0}
      >
        {showMenu && mobileView && (
          <NavbarMobile routes={navRoutes} open={mobileNavbar} openWechatModal={openWechatModal} />
        )}
      </MKBox>
      {showMenu && (
        <NavbarMenu
          anchorEl={dropdown}
          open={Boolean(dropdown)}
          onClose={() => {
            setDropdown(null);
            setDropdownName("");
          }}
          routes={navRoutes}
          dropdownName={dropdownName}
          openWechatModal={openWechatModal}
        />
      )}
      <WechatModal
        open={wechatModalOpen}
        imageUrl={wechatUrl}
        onClose={() => setWechatModalOpen(false)}
      />
    </NavbarFrame>
  );
}

// Setting default values for the props of DefaultNavbar
Navbar.defaultProps = {
  brand: "ZionLadder",
  transparent: false,
  light: false,
  action: false,
  sticky: false,
  relative: false,
  center: false,
  showMenu: true,
};

// Typechecking props for the DefaultNavbar
Navbar.propTypes = {
  brand: PropTypes.string,
  transparent: PropTypes.bool,
  light: PropTypes.bool,
  action: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      type: PropTypes.oneOf(["external", "internal"]).isRequired,
      route: PropTypes.string.isRequired,
      color: PropTypes.oneOf([
        "primary",
        "secondary",
        "info",
        "success",
        "warning",
        "error",
        "dark",
        "light",
        "default",
        "white",
      ]),
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func,
    }),
  ]),
  sticky: PropTypes.bool,
  relative: PropTypes.bool,
  center: PropTypes.bool,
  showMenu: PropTypes.bool,
};

export default Navbar;
